import {
  arrIncludesArrayItem,
  Filter,
  handleExport,
  numberInRange,
  resolveTableUpdater,
  TableContext,
  TableFooter,
  ToggleButton,
  usePersistentTableState,
} from '@eye-able/ui';
import type {
  ExportTableOptions,
  TableContextType,
  TableRef,
  TableState,
} from '@eye-able/ui';
import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  AccessorKeyColumnDef,
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  RowData,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
  type Ref,
} from 'react';
import TableItemCard from '../Card/TableItemCard';
import LoadingSpinner from '../LoadingSpinner';
import { LayoutGrid } from 'lucide-react';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { cn } from '@helpers/cn';

export type CardListView = 'list' | 'grid';

export type CardListRowRendererProps<TData extends RowData> = {
  row: TData;
  view: CardListView;
};

export type CardListRowTransformContext = {
  sorting: SortingState;
  view: CardListView;
};

export type CardListProps<TData extends RowData> = {
  actions?: ReactNode;
  data?: TData[];
  columns?: ColumnDef<TData, unknown>[];
  id?: string;
  hideFilter?: boolean;
  /**
   * Export configuration. When provided, the exposed ref's `exportTableData()`
   * downloads the filtered/sorted rows as CSV, and a download button appears in
   * the footer (mirroring the Table component).
   */
  exportOptions?: ExportTableOptions;
  /**
   * When true, the footer download button renders disabled — signalling that
   * export exists but isn't currently permitted (e.g. plan restriction).
   */
  disableExport?: boolean;
  searchColumnId?: string[];
  defaultSortBy?: string;
  defaultSortDirection?: 'asc' | 'desc';
  defaultView?: CardListView;
  disableViewToggle?: boolean;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData, index: number) => string | number;
  rowTitle: ComponentType<CardListRowRendererProps<TData>>;
  rowMain: ComponentType<CardListRowRendererProps<TData>>;
  rowTitleAction?: ComponentType<CardListRowRendererProps<TData>>;
  transformRows?: (
    rows: TData[],
    context: CardListRowTransformContext,
  ) => TData[];
  pageSize?: number;
  rowsPerPageOptions?: number[];
  hidePagination?: boolean;
  /**
   * Shows the rows as stale while fresh ones load: they are dimmed, made inert
   * (no clicks, no focus, out of the accessibility tree) and covered by a
   * spinner. The pagination controls stay live, so the user can change page
   * again without waiting for the current change to finish.
   */
  busy?: boolean;
  /**
   * Switches the list to server-driven pagination. `data` is then treated as the
   * current page instead of the full set: the list stops slicing rows itself,
   * takes the page state from the caller and reports every page change back
   * through `onPaginationChange`.
   *
   * Note that filtering, sorting and `transformRows` still run over `data`, so in
   * this mode they only cover the rows of the current page.
   */
  manualPagination?: {
    pagination: PaginationState;
    onPaginationChange: (pagination: PaginationState) => void;
    /** Total row count across all pages, used to build the page controls. */
    rowCount: number;
  };
  /**
   * Hands filtering to the caller. The list stops narrowing `data` itself and
   * reports what was asked for instead, so a server-driven list filters the
   * whole set rather than the page it happens to be showing.
   *
   * The column filters are part of it and not optional: with `data` already
   * filtered elsewhere, anything this list did not report would simply not
   * happen — the control would move and nothing would come of it.
   */
  manualFiltering?: {
    search: string;
    onSearchChange: (search: string) => void;
    columnFilters: ColumnFiltersState;
    onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  };
  /**
   * Hands sorting to the caller, for the same reason as `manualFiltering`: the
   * list stops re-ordering `data` and reports the requested sort instead.
   */
  manualSorting?: {
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
  };
};

function getColumnId<TData extends RowData>(
  column: ColumnDef<TData, unknown> | undefined,
): string | undefined {
  if (!column) return undefined;
  const accessorColumn = column as AccessorKeyColumnDef<TData, unknown>;
  return (
    column.id ??
    (typeof accessorColumn.accessorKey === 'string'
      ? accessorColumn.accessorKey
      : undefined)
  );
}

function CardListInner<TData extends RowData>(
  {
    actions,
    data = [],
    columns = [],
    id = 'card-list',
    hideFilter = false,
    exportOptions,
    disableExport = false,
    searchColumnId,
    defaultSortBy,
    defaultSortDirection = 'asc',
    defaultView = 'list',
    hidePagination = false,
    disableViewToggle = false,
    onRowClick,
    pageSize = 50,
    rowsPerPageOptions = [10, 20, 50],
    manualPagination,
    manualFiltering,
    manualSorting,
    busy = false,
    getRowId = (row, index) => {
      const candidate = (row as { id?: string | number }).id;
      return candidate ?? index;
    },
    rowTitle: RowTitle,
    rowMain: RowMain,
    rowTitleAction: RowTitleAction,
    transformRows,
  }: CardListProps<TData>,
  ref: Ref<TableRef<TData>>,
) {
  const { t } = useInlineTranslation();

  const searchKeys = useMemo(() => {
    if (searchColumnId) return searchColumnId;
    const fallback = getColumnId(columns[0]);
    return fallback ? [fallback] : [];
  }, [columns, searchColumnId]);

  const initialSortColumnId = useMemo(() => {
    const firstSortableColumn = columns.find(
      (column) => column.enableSorting !== false && getColumnId(column),
    );
    return defaultSortBy ?? getColumnId(firstSortableColumn);
  }, [columns, defaultSortBy]);

  const tableId = useMemo(() => {
    const columnsKey = columns
      .map(
        (column) =>
          (column as AccessorKeyColumnDef<TData, unknown>)?.accessorKey,
      )
      .sort()
      .join('_');

    return `${window.location.pathname}_${id}_${columnsKey}`;
  }, [columns, id]);

  const viewStorageKey = `${tableId}_view`;

  const defaultTableState = useMemo<TableState>(() => {
    return {
      sorting: initialSortColumnId
        ? [
            {
              id: initialSortColumnId,
              desc: defaultSortDirection === 'desc',
            },
          ]
        : [],
      columnFilters: [],
      columnVisibility: {},
    };
  }, [defaultSortDirection, initialSortColumnId]);

  const [view, setView] = useState<CardListView>(() => {
    const storedView = localStorage.getItem(viewStorageKey);

    return storedView === 'grid' || storedView === 'list'
      ? storedView
      : defaultView;
  });
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize,
    },
  );

  const globalFilter = manualFiltering?.search ?? internalGlobalFilter;
  const setGlobalFilter = useCallback(
    (updater: Updater<string>) => {
      if (manualFiltering) {
        manualFiltering.onSearchChange(
          resolveTableUpdater(updater, manualFiltering.search),
        );
        return;
      }
      setInternalGlobalFilter((previous) =>
        resolveTableUpdater(updater, previous),
      );
    },
    [manualFiltering],
  );

  // In manual mode the caller owns the page state and the total row count; the
  // rows in `data` are already the current page.
  const pagination = manualPagination?.pagination ?? internalPagination;
  const setPagination = useCallback(
    (update: (previous: PaginationState) => PaginationState) => {
      if (manualPagination) {
        manualPagination.onPaginationChange(
          update(manualPagination.pagination),
        );
        return;
      }
      setInternalPagination(update);
    },
    [manualPagination],
  );
  const [tableState, setTableState] = usePersistentTableState(
    tableId,
    defaultTableState,
  );

  useEffect(() => {
    localStorage.setItem(viewStorageKey, view);
  }, [viewStorageKey, view]);

  /**
   * The state the table runs on and the toolbar renders from — one object, so
   * the two cannot disagree. Whatever the caller drives has to be in here: the
   * sort control and the filter dialog read their current value out of it, and a
   * control that kept reporting the default would snap back to it every time the
   * user picked something else.
   */
  const effectiveTableState = useMemo<TableState>(
    () => ({
      ...tableState,
      ...(manualSorting ? { sorting: manualSorting.sorting } : {}),
      ...(manualFiltering
        ? { columnFilters: manualFiltering.columnFilters }
        : {}),
    }),
    [manualFiltering, manualSorting, tableState],
  );

  const table = useReactTable<TData>({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters: effectiveTableState.columnFilters,
      columnVisibility: effectiveTableState.columnVisibility,
      sorting: effectiveTableState.sorting,
    },
    // With the caller driving these, `data` is already searched and ordered —
    // re-doing either here would only re-apply it to the current page.
    manualFiltering: !!manualFiltering,
    manualSorting: !!manualSorting,
    // `data` is one page in manual mode, so the total comes from the server.
    // The search announcement in `Filter` reads it via `getRowCount()`.
    rowCount: manualPagination?.rowCount,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: (updater) => {
      if (manualFiltering) {
        manualFiltering.onColumnFiltersChange(
          resolveTableUpdater(updater, manualFiltering.columnFilters),
        );
        return;
      }
      setTableState((prev) => ({
        ...prev,
        columnFilters: resolveTableUpdater(updater, prev.columnFilters ?? []),
      }));
    },
    onColumnVisibilityChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnVisibility: resolveTableUpdater(
          updater,
          prev.columnVisibility ?? {},
        ),
      })),
    onSortingChange: (updater) => {
      if (manualSorting) {
        manualSorting.onSortingChange(
          resolveTableUpdater(updater, manualSorting.sorting),
        );
        return;
      }
      setTableState((prev) => ({
        ...prev,
        sorting: resolveTableUpdater(updater, prev.sorting ?? []),
      }));
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const query = String(filterValue ?? '')
        .trim()
        .toLowerCase();
      if (!query) return true;

      return searchKeys.some((key) =>
        String(row.getValue(key) ?? '')
          .toLowerCase()
          .includes(query),
      );
    },
    filterFns: {
      arrIncludesArrayItem: (row, columnId, filterValues, addMeta) => {
        if (arrIncludesArrayItem(row, columnId, filterValues, addMeta)) {
          return true;
        }

        if (!Array.isArray(filterValues) || filterValues.length === 0) {
          return false;
        }

        const cellValue = row.getValue(columnId);
        if (Array.isArray(cellValue)) {
          return cellValue.some((value) => filterValues.includes(value));
        }

        if (typeof cellValue === 'string') {
          const normalizedCellValues = cellValue
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          return normalizedCellValues.some((value) =>
            filterValues.includes(value),
          );
        }

        return false;
      },
      numberInRange,
    },
  });

  const filteredAndSortedRows = table.getSortedRowModel().rows;

  const renderedRows = useMemo(() => {
    const rows = filteredAndSortedRows.map((tableRow) => tableRow.original);

    if (!transformRows) {
      return rows;
    }

    return transformRows(rows, {
      sorting: effectiveTableState.sorting,
      view,
    });
  }, [filteredAndSortedRows, effectiveTableState.sorting, transformRows, view]);

  // The server already returned just this page in manual mode.
  const totalRowCount = manualPagination?.rowCount ?? renderedRows.length;

  const paginatedRows = useMemo(() => {
    if (manualPagination) return renderedRows;

    const start = pagination.pageIndex * pagination.pageSize;
    return renderedRows.slice(start, start + pagination.pageSize);
  }, [
    manualPagination,
    pagination.pageIndex,
    pagination.pageSize,
    renderedRows,
  ]);

  useEffect(() => {
    const maxPageIndex = Math.max(
      Math.ceil(totalRowCount / pagination.pageSize) - 1,
      0,
    );

    if (pagination.pageIndex > maxPageIndex) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: maxPageIndex,
      }));
    }
  }, [pagination.pageIndex, pagination.pageSize, setPagination, totalRowCount]);

  const exportTableData = useCallback(() => {
    if (!exportOptions) return;
    handleExport(table.getSortedRowModel().rows, columns, exportOptions);
  }, [columns, exportOptions, table]);

  useImperativeHandle(ref, () => ({ table, tableId, exportTableData }), [
    table,
    tableId,
    exportTableData,
  ]);

  const contextValue = useMemo<Partial<TableContextType<TData>>>(() => {
    return {
      globalFilter,
      setGlobalFilter,
      table,
      tableState: effectiveTableState,
    };
  }, [globalFilter, setGlobalFilter, table, effectiveTableState]);

  return (
    <TableContext.Provider
      value={contextValue as unknown as TableContextType<unknown>}
    >
      <div className='eye:flex eye:w-full eye:flex-col'>
        {!hideFilter && (
          <Filter
            disableFullscreen
            disableHideShowColumns
            actions={
              !disableViewToggle ? (
                <div className='eye:flex eye:items-center eye:gap-4xs eye:mr-2xs'>
                  {actions}
                  <ToggleButton
                    pressed={view === 'grid'}
                    aria-label={view === 'grid' ? t('listView') : t('gridView')}
                    className='eye:bg-white'
                    onPressedChange={(pressed) => {
                      setView(pressed ? 'grid' : 'list');
                    }}
                  >
                    <LayoutGrid className='eye:stroke-[1.5]' aria-hidden />
                  </ToggleButton>
                </div>
              ) : (
                actions
              )
            }
          />
        )}

        {renderedRows.length === 0 ? (
          <div className='eye:body-m eye:py-16 eye:text-center eye:text-grey-120'>
            <T k='noResultsFound' />
          </div>
        ) : (
          <div className='eye:relative'>
            <ul
              aria-busy={busy}
              // `inert` keeps clicks, focus and the accessibility tree out of the
              // stale cards. React 18 has no typed prop for it, and the empty
              // string is the attribute's "present" form.
              {...(busy ? { inert: '' } : {})}
              className={cn(
                view === 'grid'
                  ? 'eye:grid eye:grid-cols-1 eye:gap-2xs eye:md:grid-cols-2 eye:xl:grid-cols-3'
                  : 'eye:flex eye:flex-col eye:gap-2xs',
                busy && 'eye:opacity-40 eye:transition-opacity',
              )}
            >
              {paginatedRows.map((row, index) => {
                const rowId = getRowId(
                  row,
                  pagination.pageIndex * pagination.pageSize + index,
                );
                const rowRendererProps = { row, view };

                return (
                  <li key={rowId} className='eye:flex eye:flex-col'>
                    <TableItemCard
                      title={<RowTitle {...rowRendererProps} />}
                      titleAction={
                        RowTitleAction ? (
                          <RowTitleAction {...rowRendererProps} />
                        ) : null
                      }
                      main={<RowMain {...rowRendererProps} />}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    />
                  </li>
                );
              })}
            </ul>

            {busy && (
              <div
                className='eye:absolute eye:inset-0 eye:flex eye:items-start eye:justify-center eye:pt-32'
                data-testid='card-list-busy-overlay'
              >
                <LoadingSpinner height={120} />
              </div>
            )}

            {/* Deliberately outside the inert wrapper: paging stays available so
                a second page change can interrupt the one in flight. */}
            {!hidePagination && (
              <TableFooter
                page={pagination.pageIndex + 1}
                pageCount={Math.ceil(totalRowCount / pagination.pageSize)}
                pageSize={pagination.pageSize}
                totalItems={totalRowCount}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))
                }
                onPageSizeChange={(size) =>
                  setPagination((prev) => ({ ...prev, pageSize: size }))
                }
                rowsPerPageOptions={rowsPerPageOptions}
                exportTableData={exportOptions ? exportTableData : undefined}
                disableExport={disableExport}
              />
            )}
          </div>
        )}
      </div>
    </TableContext.Provider>
  );
}

const CardList = forwardRef(CardListInner) as unknown as <
  TData extends RowData,
>(
  props: CardListProps<TData> & { ref?: Ref<TableRef<TData>> },
) => JSX.Element;

export default CardList;
